import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../../core/services/auth.service';
import { BearerToken } from '../../../core/models/bearer-token';
import { MatDialog } from '@angular/material/dialog';
import { MfaHelpDialogComponent } from './mfa-help-dialog.component';
import { observableToBeFn } from 'rxjs/internal/testing/TestScheduler';
import { Observable } from 'rxjs';

enum MfaMode{
  Login,
  Setup,
  Remove
}

@Component({
  selector: 'app-mfa',
  templateUrl: './mfa.component.html',
  styleUrl: './mfa.component.scss'
})
export class MfaComponent {

  loading = false;
  errorCode : string | undefined;
  errorMessage: string | undefined;
  mfaFormSubmitted: boolean = false;

  // Used to determine if we are setting up, login in with, or removing MFA
  mfaMode = MfaMode;
  _mfaMode! : MfaMode;

  // Used for setup
  totpUri: string | undefined;
  totpSecret: string | undefined;
  isMfaEnforced: boolean | undefined;

  mfaCode = new FormControl('', [Validators.required]);
  mfaForm = new FormGroup({
    mfaCode: this.mfaCode
  });
  mfaEnabled: boolean | undefined;

  constructor(private authService : AuthService, private router: Router, private dialog: MatDialog) {
    this.setupMfaConfig();
  }


  /**
   * Determine if we are in setup, login, or remove mode
   */
  getMfaMode(){

    // If mfa is not enabled, or we are setting up after a reset (and thus have a totpSecret
    var mfaIsSetup = !this.mfaEnabled || this.totpSecret;

    switch (this.router.url) {
      case '/mfa-setup':
        // If MFA is already setup, act like we are in login mode
        // This will result in authRedirect sending us /Home
        this._mfaMode = mfaIsSetup ? MfaMode.Setup : MfaMode.Login;
        break;
      case '/mfa-remove':
        this._mfaMode = MfaMode.Remove;
        break;
      case '/mfa':
        // If enabled, only prompt for code
        // Otherwise supply QR code to scan
        this._mfaMode = mfaIsSetup ? MfaMode.Setup : MfaMode.Login;
        break;
    }
  }


  /**
   * Fetch MFA config (QR code, secret, enabled, etc.)
   * and set up the component state
   */
  setupMfaConfig() {

    this.loading = true;

    this.authService.GetMfaConfig(true).subscribe({
      next: totpConfig => {
        this.totpUri = totpConfig.totpUri;
        this.totpSecret = totpConfig.totpSecret;
        this.isMfaEnforced = totpConfig.isMfaEnforced;
        this.mfaEnabled = totpConfig.mfaEnabled;

        this.getMfaMode();

        // Handle any redirect requirements
        // i.e if we are on an invalid page for setup/remove
        // If the user is on an invalid page,
        // They will see a loading spinner until they are redirected
        // Its sort of gross, but that should only happen
        // if they manually navigate here
        this.authRedirect();

        this.loading = false;
      }
    });
  }

  authRedirect(force: boolean = false){
    if (this.authService.isLoggedIn()) {
      // Don't navigate away if we are setting up or removing MFA
      if(force || this._mfaMode == MfaMode.Login){
          this.router.navigate(['home']);
      }
    }
    else if (!this.authService.isPartiallyLoggedIn()) {
      this.router.navigate(['login']);
    }
  };

  help(){
    // Open the help dialog

  this.dialog.open(MfaHelpDialogComponent, {
    panelClass: 'dialog-panel-large'
  });
  }

  logout(){
    this.authService.logOut();
    this.router.navigate(['login']);
  }

  completeMfaForm() {

    this.loading = true;

    this.mfaFormSubmitted = true

    var mfaCode = this.mfaForm.value.mfaCode ?? '';

    var mfaObservable : Observable<any> | undefined;
    switch(this._mfaMode){
      case MfaMode.Setup:
        this.enableMfa(mfaCode);
        break;
      case MfaMode.Remove:
        this.removeMfa(mfaCode);
        break;
      default:
        this.loginWithMfaCode(mfaCode);
        break;
    }
  }


  handleMfaError(error: any){
    // if the error is a 400 and can be cast to BearerToken, show the error_description
    // otherwise show a generic error message
    if (error.status === 400) {
      var bearerToken = error.error as BearerToken;
      this.errorMessage = bearerToken.error_description;
      this.errorCode = bearerToken.error;
      
    } else {
      this.errorMessage = 'An error occurred while trying to log in. Please try again later.';
    }
     
     this.mfaCode.setErrors({'mfaInvalid': true});
 }

  enableMfa(mfaCode: string){

    this.authService.enableMfa(mfaCode).subscribe({
      next: () => {
        this.loginWithMfaCode(mfaCode);
      },
      error: (error) => {
        this.handleMfaError(error);
        this.loading = false;
        
      }
  });
  }

  removeMfa(mfaCode: string){
    this.authService.removeMfa(mfaCode).subscribe({
      next: () => {
        this.authRedirect(true);
        this.loading = false;
      },
      error: (error) => {
        this.handleMfaError(error);
        this.loading = false;
      }});
  }

  loginWithMfaCode(mfaCode: string){
      this.authService.loginWithMfaCode(mfaCode).subscribe({
        next: () => {
          this.authRedirect(true);
          this.loading = false;
        },
        error: (error) => {
          this.handleMfaError(error);
          this.loading = false;
        }});
  }



}
